import React, { useEffect, useRef, useState } from "react";
import {
  ClipboardList,
  Eye,
  FileUp,
  Mail,
  Phone,
  Receipt,
  FileText,
  Send,
  Upload,
  X,
} from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "react-toastify";

const HOSPITAL_API_BASE = process.env.REACT_APP_SURGERY_STATUS;

const getDocumentType = (file) => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "jpg" || ext === "jpeg") return "jpg";
  if (ext === "png") return "png";
  if (ext === "webp") return "webp";
  return ext || "png";
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const fetchDocumentUrl = async (fileName) => {
  const token = localStorage.getItem("auth_token");
  const { data } = await axios.get(
    `${HOSPITAL_API_BASE}document/getDocumentURL/${encodeURIComponent(fileName)}`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return data?.successObject;
};

const extractUploadedFileName = (result, fallback) => {
  if (typeof result === "string") return result;
  if (result?.uploadBillName) return result.uploadBillName;
  if (result?.uploadReportName) return result.uploadReportName;
  if (result?.fileName) return result.fileName;
  return fallback;
};

const DocumentModal = ({ url, label, onClose }) => {
  if (!url) return null;
  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#0A4C8A] to-[#00B58F] shrink-0" />
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E8E8E8] shrink-0">
          <p className="text-[15px] font-semibold text-[#0B4D76] truncate pr-4">
            {label}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-[#F4F8FB] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-[#F4F6F8] min-h-0">
          {isImage ? (
            <img src={url} alt={label} className="max-w-full mx-auto block p-4" />
          ) : (
            <iframe
              src={url}
              title={label}
              className="w-full h-full min-h-[600px] border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const formatDisplayDate = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(/\.0$/, ""));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return format(parsed, "dd MMM yyyy");
};

const DetailItem = ({ label, value }) => {
  const display = value || "—";
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-[#9CA3AF]">{label}</p>
      <p className="text-[13px] font-medium text-[#374151] mt-0.5 break-words leading-snug">
        {display}
      </p>
    </div>
  );
};

const ContactRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon size={15} className="text-[#0B4D76] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] text-[#9CA3AF]">{label}</p>
      <p className="text-[13px] font-medium text-[#374151] truncate" title={value || "—"}>
        {value || "—"}
      </p>
    </div>
  </div>
);

const UploadCard = ({
  label,
  description,
  icon: Icon,
  fileName,
  uploading,
  viewing,
  disabled,
  uploadLabel = "Upload",
  replaceLabel = "Replace",
  onSelect,
  onView,
}) => {
  const inputRef = useRef(null);

  return (
    <div className="rounded-xl border border-[#DDE8F2] bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#EAF3FB] flex items-center justify-center shrink-0">
          <Icon size={18} className="text-[#0B4D76]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-[#111827]">{label}</p>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">{description}</p>
          {fileName && (
            <p className="text-[12px] font-medium text-[#0B4D76] mt-2 truncate">
              {fileName}
            </p>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = "";
        }}
      />
      <div className={`mt-4 grid gap-2 ${fileName ? "grid-cols-2" : "grid-cols-1"}`}>
        {fileName && (
          <button
            type="button"
            disabled={viewing}
            onClick={onView}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-[#0A4C8A] text-[#0A4C8A] bg-[#F4F8FB] hover:bg-[#EAF3FB] transition-all disabled:opacity-60"
          >
            {viewing ? (
              <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Eye size={15} />
            )}
            View
          </button>
        )}
        <button
          type="button"
          disabled={uploading || disabled}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-[#0A4C8A] text-[#0A4C8A] bg-white hover:bg-gradient-to-r hover:from-[#0A4C8A] hover:to-[#00B58F] hover:text-white hover:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload size={15} />
          )}
          {fileName ? replaceLabel : uploadLabel}
        </button>
      </div>
    </div>
  );
};

const ReportsUploadCard = ({
  reports,
  uploading,
  viewingFileName,
  disabled,
  onAdd,
  onView,
}) => {
  const inputRef = useRef(null);

  return (
    <div className="rounded-xl border border-[#DDE8F2] bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#EAF3FB] flex items-center justify-center shrink-0">
          <FileText size={18} className="text-[#0B4D76]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-[#111827]">Upload Report</p>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">
            Add one or more medical or service reports.
          </p>
        </div>
      </div>

      {reports.length > 0 && (
        <ul className="mt-4 space-y-2">
          {reports.map((report, index) => (
            <li
              key={report.id}
              className="flex items-center gap-2 p-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC]"
            >
              <span className="flex-1 min-w-0">
                <p className="text-[11px] text-[#9CA3AF]">Report {index + 1}</p>
                <p className="text-[13px] font-medium text-[#374151] truncate">
                  {report.fileName}
                </p>
              </span>
              <button
                type="button"
                disabled={viewingFileName === report.fileName}
                onClick={() => onView(report.fileName, `Report ${index + 1}`)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-[#0A4C8A] text-[#0B4D76] bg-white hover:bg-[#F4F8FB] transition-all disabled:opacity-60 shrink-0"
              >
                {viewingFileName === report.fileName ? (
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Eye size={14} />
                )}
                View
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onAdd(file);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={uploading || disabled}
        onClick={() => inputRef.current?.click()}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-semibold border border-[#0A4C8A] text-[#0A4C8A] bg-white hover:bg-gradient-to-r hover:from-[#0A4C8A] hover:to-[#00B58F] hover:text-white hover:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {uploading ? (
          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <Upload size={15} />
        )}
        {reports.length > 0 ? "Add another report" : "Upload report"}
      </button>
    </div>
  );
};

export default function BillingUploadSidebar({
  isOpen,
  onClose,
  onApprovalSuccess,
  serviceRecord,
  patientSummary,
  loading,
}) {
  const [billFileName, setBillFileName] = useState("");
  const [reportFiles, setReportFiles] = useState([]);
  const [uploadingBill, setUploadingBill] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [viewingBill, setViewingBill] = useState(false);
  const [viewingReportFileName, setViewingReportFileName] = useState("");
  const [documentModal, setDocumentModal] = useState(null);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [approvalSubmitted, setApprovalSubmitted] = useState(false);

  const serviceId = serviceRecord?.id;

  useEffect(() => {
    if (!isOpen) {
      setBillFileName("");
      setReportFiles([]);
      setDocumentModal(null);
      setViewingReportFileName("");
      setApprovalSubmitted(false);
      return;
    }
    setBillFileName(serviceRecord?.uploadBillName || "");
    if (serviceRecord?.uploadReportName) {
      setReportFiles([
        {
          id: `existing-${serviceRecord.uploadReportName}`,
          fileName: serviceRecord.uploadReportName,
        },
      ]);
    } else {
      setReportFiles([]);
    }
  }, [isOpen, serviceRecord?.id, serviceRecord?.uploadBillName, serviceRecord?.uploadReportName]);

  if (!isOpen) return null;

  const uploadBillDocument = async (file) => {
    if (!HOSPITAL_API_BASE) {
      throw new Error("Hospital API URL is not configured.");
    }
    if (!serviceId) {
      throw new Error("Service ID not found. Please create billing service first.");
    }

    const token = localStorage.getItem("auth_token");
    const documentType = getDocumentType(file);
    const documentByte = await fileToBase64(file);
    const payload = {
      documentType,
      documentByte,
      id: serviceId,
    };

    const { data } = await axios.put(
      `${HOSPITAL_API_BASE}document/uploadBill/${serviceId}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    return extractUploadedFileName(data?.successObject, file.name);
  };

  const uploadReportDocument = async (file) => {
    if (!HOSPITAL_API_BASE) {
      throw new Error("Hospital API URL is not configured.");
    }
    if (!serviceId) {
      throw new Error("Service ID not found. Please create billing service first.");
    }

    const token = localStorage.getItem("auth_token");
    const documentType = getDocumentType(file);
    const documentByte = await fileToBase64(file);
    const payload = [
      {
        documentType,
        documentByte,
        id: serviceId,
      },
    ];

    const { data } = await axios.put(
      `${HOSPITAL_API_BASE}document/uploadReport/${serviceId}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    if (Array.isArray(data?.successList) && data.successList.length > 0) {
      return data.successList.map((item, index) =>
        extractUploadedFileName(item, `${file.name}-${index + 1}`)
      );
    }

    return [extractUploadedFileName(data?.successObject, file.name)];
  };

  const handleViewDocument = async (fileName, label, setViewing) => {
    if (!fileName) return;
    if (typeof setViewing === "function") {
      setViewing(true);
    } else {
      setViewingReportFileName(fileName);
    }
    try {
      const url = await fetchDocumentUrl(fileName);
      if (url) {
        setDocumentModal({ url, label });
      } else {
        toast.error("Document URL not found.");
      }
    } catch (error) {
      console.error("Failed to open document:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Failed to open document."
      );
    } finally {
      if (typeof setViewing === "function") {
        setViewing(false);
      } else {
        setViewingReportFileName("");
      }
    }
  };

  const handleBillUpload = async (file) => {
    setUploadingBill(true);
    try {
      const uploadedName = await uploadBillDocument(file);
      setBillFileName(String(uploadedName || file.name));
      toast.success("Bill uploaded successfully.");
    } catch (error) {
      console.error("Bill upload failed:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload bill."
      );
    } finally {
      setUploadingBill(false);
    }
  };

  const handleReportUpload = async (file) => {
    setUploadingReport(true);
    try {
      const uploadedNames = await uploadReportDocument(file);
      const names = Array.isArray(uploadedNames) ? uploadedNames : [uploadedNames];
      setReportFiles((prev) => [
        ...prev,
        ...names.map((fileName, index) => ({
          id: `${Date.now()}-${index}-${fileName}`,
          fileName: String(fileName),
        })),
      ]);
      toast.success(
        names.length > 1
          ? `${names.length} reports uploaded successfully.`
          : "Report uploaded successfully."
      );
    } catch (error) {
      console.error("Report upload failed:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload report."
      );
    } finally {
      setUploadingReport(false);
    }
  };

  const handleSendForApproval = async () => {
    if (!serviceId) {
      toast.error("Billing service is not ready yet.");
      return;
    }

    const modifiedBy =
      patientSummary?.modifiedBy ||
      serviceRecord?.hospitalId ||
      localStorage.getItem("hospitalid") ||
      "";
    const userRoleCode =
      "CE";

    setSubmittingApproval(true);
    try {
      const token = localStorage.getItem("auth_token");
      const { data } = await axios.put(
        `${HOSPITAL_API_BASE}approval/adminApproval/`,
        {
          id: serviceId,
          billingStatus: 1,
          modifiedBy,
          userRoleCode,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (data?.responseCode && data.responseCode !== "200") {
        throw new Error(data?.responseMessage || "Failed to send for approval.");
      }

      toast.success("Sent for approval successfully.");

      if (Number(data?.successObject?.billingStatus) === 1) {
        onApprovalSuccess?.(data.successObject);
      } else {
        setApprovalSubmitted(true);
      }
    } catch (error) {
      console.error("Admin approval failed:", error);
      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send for approval."
      );
    } finally {
      setSubmittingApproval(false);
    }
  };

  const displayName = patientSummary?.patientName || "—";
  const enquiryId =
    patientSummary?.serviceEnquiryId || serviceRecord?.serviceEnquiryId || "—";
  const mobile = patientSummary?.patientContactNumber || null;
  const email = patientSummary?.patientEmail || null;
  const procedureName = patientSummary?.procedureName || null;
  const hospitalName = patientSummary?.hospitalName || null;
  const enquiryDate = formatDisplayDate(patientSummary?.createdDate);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <aside className="fixed top-0 right-0 z-50 h-full w-full max-w-[640px] sm:max-w-[720px] bg-[#F3F6F9] shadow-2xl flex flex-col">
        <div className="h-1 bg-gradient-to-r from-[#0A4C8A] to-[#00B58F] shrink-0" />

        <div className="shrink-0 bg-white px-5 py-4 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0A4C8A] to-[#00B58F] flex items-center justify-center shrink-0">
                <FileUp size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-[16px] font-bold text-[#0B4D76] leading-tight truncate">
                  Billing Service
                </h2>
                <p className="text-[12px] text-[#9CA3AF]">Enquiry #{enquiryId}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#374151] transition-colors shrink-0"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 px-6">
              <div className="w-10 h-10 border-2 border-[#00B58F] border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-[14px] text-[#6B7280]">Creating billing service…</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
                <div className="px-5 py-4 flex items-center gap-3 border-b border-[#F3F4F6]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0A4C8A] to-[#00B58F] flex items-center justify-center text-white shrink-0">
                    <ClipboardList size={18} />
                  </div>
                  <p className="text-[15px] font-bold text-[#111827] leading-tight break-words">
                    {displayName}
                  </p>
                </div>

                <div className="px-5 py-4 grid grid-cols-2 gap-4 border-b border-[#F3F4F6]">
                  <ContactRow icon={Phone} label="Mobile" value={mobile} />
                  <ContactRow icon={Mail} label="Email" value={email} />
                </div>

                <div className="px-5 py-4 grid grid-cols-3 gap-4">
                  <DetailItem label="Procedure" value={procedureName} />
                  <DetailItem label="Enquiry date" value={enquiryDate} />
                  <DetailItem label="Hospital" value={hospitalName} />
                </div>
              </div>

              <div className="space-y-3">
                <UploadCard
                  label="Upload Bill"
                  description="Upload the patient billing document."
                  icon={Receipt}
                  fileName={billFileName}
                  uploading={uploadingBill}
                  viewing={viewingBill}
                  disabled={!serviceId}
                  onSelect={handleBillUpload}
                  onView={() =>
                    handleViewDocument(billFileName, "Bill Document", setViewingBill)
                  }
                />
                <ReportsUploadCard
                  reports={reportFiles}
                  uploading={uploadingReport}
                  viewingFileName={viewingReportFileName}
                  disabled={!serviceId}
                  onAdd={handleReportUpload}
                  onView={(fileName, label) =>
                    handleViewDocument(fileName, label)
                  }
                />
              </div>
            </div>
          )}
        </div>

        {!loading && (
          <div className="shrink-0 border-t border-[#E5E7EB] bg-white px-5 py-4">
            <button
              type="button"
              onClick={handleSendForApproval}
              disabled={!serviceId || submittingApproval || approvalSubmitted}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-semibold text-white bg-gradient-to-r from-[#0A4C8A] to-[#00B58F] hover:opacity-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submittingApproval ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={16} />
              )}
              {approvalSubmitted ? "Sent for Approval" : "Send for Approval"}
            </button>
          </div>
        )}
      </aside>

      {documentModal && (
        <DocumentModal
          url={documentModal.url}
          label={documentModal.label}
          onClose={() => setDocumentModal(null)}
        />
      )}
    </>
  );
}
